import { Injectable } from '@angular/core';
import Utils from '../../../app/shared/utils/utils';
import { GameScoringSystemTypeConfigOptions } from '../../../config/game-scoring-system-type-config.interface';
import { GameScoringSystemCategory } from '../../game/enum/game-scoring-system-category.enum';
import { FormControlOptions } from '../interfaces/form-control-options.interface';
import { FormControlSelectOptions } from '../interfaces/form-control-select-options.interface';
import { Dictionary } from '../models/dictionary';
import { GameUtilHelper } from '../models/helper/game-util-helper';
import { ConfigurationService } from '../services/configuration.service';

@Injectable({
  providedIn: 'root'
})
export class GameScoringSystemTypeUtils {

  private scoringSystemTypeValues;

  constructor(
    private readonly config: ConfigurationService,
    private readonly gameUtilHelper: GameUtilHelper
  ) {
  }

  private getScoringSystemTypes(): GameScoringSystemTypeConfigOptions {
    if (this.config.isLoadingConfig()) {
      return undefined;
    }

    const config = this.config.getScoringSystemTypeConfig();
    if (config) {
      return Utils.clone(config);
    }

    return undefined;
  }

  getTypeConfig(type: number) {
    return this.getScoringSystemTypes()[ type ];
  }

  getLabel(scoringSystemType: string | number) {
    if (this.getScoringSystemTypes() && this.isValid(scoringSystemType)) {
      return this.getScoringSystemTypes()[ Number(scoringSystemType) ].label;
    }

    return '';
  }

  hasField(scoringSystemType: string | number, field: string): boolean {
    if (this.getScoringSystemTypes()) {
      return Object.keys(this.getScoringSystemTypes()[ Number(scoringSystemType) ].fields).indexOf(field) >= 0;
    }

    return false;
  }

  getFieldOptions(scoringSystemType: string | number, field: string, defaultOptions: FormControlOptions<any>): FormControlOptions<any> {
    if (this.getScoringSystemTypes()) {
      const options = this.hasField(scoringSystemType, field) ? Utils.clone(this.getScoringSystemTypes()[ Number(scoringSystemType) ].fields[ field ]) : {};

      for (const optionName in defaultOptions) {
        if (Utils.isDefined(defaultOptions[ optionName ])) {
          options[ optionName ] = Utils.isDefined(options[ optionName ]) ? options[ optionName ] : defaultOptions[ optionName ];
        }
      }

      options.key = defaultOptions.key || field;
      options.hidden = Utils.isDefined(defaultOptions.hidden) ? defaultOptions.hidden : !this.hasField(scoringSystemType, field);

      return options;
    }

    return {};
  }

  getTypeValues(): number[] {
    if (this.getScoringSystemTypes()) {
      this.scoringSystemTypeValues = this.scoringSystemTypeValues || Object.keys(this.getScoringSystemTypes())
        .filter(Number)
        .map(scoringSystemType => Number(scoringSystemType));

      return Utils.clone(this.scoringSystemTypeValues);
    }

    return [];
  }

  getCategoryOfType(type: number): string {
    const config = this.getScoringSystemTypes()[ type ];

    return config && config.category;
  }

  getCategoryValues(): string[] {
    return Object.values(GameScoringSystemCategory);
  }

  getDefaultType(): number {
    return this.getTypeValues()[ 0 ];
  }

  getDefaultCategory(): string {
    return this.getCategoryValues()[ 0 ];
  }

  isValid(scoringSystemType: string | number): boolean {
    return !isNaN(Number(scoringSystemType)) && this.getTypeValues().indexOf(Number(scoringSystemType)) >= 0;
  }

  getTypeSelectOptions(): FormControlSelectOptions<number> {
    return this.getTypeValues()
      .map(scoringSystemType => {
        return {
          value: scoringSystemType,
          label: this.getLabel(scoringSystemType)
        };
      });
  }

  getCategorySelectOptions(): FormControlSelectOptions<string> {
    return this.getCategoryValues()
      .map(value => ({ value, label: value.toUpperCase() }));
  }

  getModules(scoringSystemType: string | number): string[] {
    if (this.getScoringSystemTypes()) {
      return Utils.clone(this.getScoringSystemTypes()[ Number(scoringSystemType) ].modules);
    }

    return [];
  }

  isModuleEnabled(scoringSystemType: string | number, module: string): boolean {
    return this.getModules(scoringSystemType).indexOf(module) >= 0;
  }

  createDictionary(levelType: string | number, key: 'parameters' | 'textDictionary' | 'assetDictionary'): any[] {

    const types = this.getScoringSystemTypes();

    if (levelType && types) {
      const dict: Dictionary<any> = types[ Number(levelType) ];

      return this.gameUtilHelper.createDictionary(dict, key);
    }

    return [];
  }
}
